import { z } from "zod"

import { branchIdSchema, legalEntityIdSchema, tenantIdSchema } from "./identifiers.js"

export const financialReportKindV1Schema = z.enum([
  "profit_and_loss",
  "balance_sheet",
  "cash",
  "accounts_receivable_aging",
  "accounts_payable_aging",
  "tax_review",
  "branch_profitability",
  "purchasing",
  "inventory_valuation",
  "inventory_exceptions",
  "fleet_maintenance",
  "reconciliation",
  "accounting_periods",
])

export const financialReportRequestV1Schema = z.object({
  reportVersion: z.literal(1),
  kind: financialReportKindV1Schema,
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema.nullable(),
  fromDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  toDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  cursor: z.number().int().min(0).max(10_000),
  pageSize: z.number().int().min(1).max(50),
}).strict().refine((value) => value.toDate >= value.fromDate, {
  message: "Invalid report window.",
})

export const financialReportV1Schema = z.object({
  reportVersion: z.literal(1),
  tenantId: tenantIdSchema,
  kind: financialReportKindV1Schema,
  observedAt: z.string().datetime({ offset: true }),
  authoritativeSource: z.literal("frappe_erpnext"),
  totals: z.object({
    debitMinor: z.number().int().safe(),
    creditMinor: z.number().int().safe(),
    netMinor: z.number().int().safe(),
  }).strict(),
  items: z.array(z.object({
    itemId: z.string().min(1).max(140),
    occurredOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    legalEntityId: legalEntityIdSchema,
    branchId: branchIdSchema.nullable(),
    category: z.string().min(1).max(80),
    status: z.string().min(1).max(80),
    reference: z.string().min(1).max(140),
    debitMinor: z.number().int().safe(),
    creditMinor: z.number().int().safe(),
    currency: z.string().regex(/^[A-Z]{3}$/),
  }).strict()).max(50),
  nextCursor: z.number().int().min(0).max(10_000).nullable(),
  hasMore: z.boolean(),
}).strict()

export type FinancialReportKindV1 = z.infer<typeof financialReportKindV1Schema>
export type FinancialReportRequestV1 = z.infer<
  typeof financialReportRequestV1Schema
>
export type FinancialReportV1 = z.infer<typeof financialReportV1Schema>
